import React from 'react';
import { useTranslation } from 'react-i18next';
import { Profile2User, ProfileTick } from 'iconsax-react';

import TitleText from '../../../components/atoms/TitleText';
import InfoText from '../../../components/atoms/InfoText';
import { SupportTicket } from '../../../types/support';
import { formatCustomerType } from './utils';

export interface TicketUserCardsProps {
  ticket: SupportTicket;
  testIdPrefix?: string;
  className?: string;
}

export const TicketUserCards: React.FC<TicketUserCardsProps> = ({
  ticket,
  testIdPrefix = 'ticket-info',
  className = '',
}) => {
  const { t } = useTranslation();

  // Requester Data from Payload
  const requesterName = ticket?.requesterName || '---';
  const requesterHandle = ticket?.requesterUserName || '';
  const requesterPhoto = ticket?.requesterPhoto || '';
  const requesterEmail = ticket?.requesterEmail || '---';
  const requesterType = ticket?.requesterType || '';

  // Request Applies To (Against User) Data from Payload
  const againstUser = ticket?.againstUser;
  const sellerName = againstUser?.customerName || '---';
  const sellerHandle = againstUser?.customerUserName || '';
  const sellerPhoto = againstUser?.customerPhoto || '';
  const sellerEmail = againstUser?.customerEmail || '---';
  const sellerType = againstUser?.customerType || '';

  const showAppliesCard = Boolean(ticket?.againstUser);

  return (
    <div
      className={`grid grid-cols-1 ${
        showAppliesCard ? 'md:grid-cols-2' : ''
      } gap-4 ${className} min-w-0`}
      data-testid={`${testIdPrefix}-cards-grid`}
    >
      {/* Requester Card */}
      <div
        className="bg-soft-background dark:bg-dark-surface-secondary rounded-2xl p-4 sm:p-5 flex flex-col justify-between"
        data-testid={`${testIdPrefix}-requester-card`}
      >
        <div
          className="flex items-center gap-2 mb-3.5"
          data-testid={`${testIdPrefix}-requester-header`}
        >
          <Profile2User
            size={20}
            variant="Bold"
            className="text-light-information dark:text-dark-information shrink-0"
            data-testid={`${testIdPrefix}-requester-icon`}
          />
          <TitleText
            text={t('support.requester')}
            size="base"
            className="!font-bold !text-light-text-primary dark:!text-dark-text-primary"
            data-testid={`${testIdPrefix}-requester-title`}
          />
        </div>

        <div
          className="flex items-center gap-3.5 min-w-0"
          data-testid={`${testIdPrefix}-requester-body`}
        >
          <div
            className="w-14 h-14 rounded-full overflow-hidden shrink-0 border border-gray-100 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 flex items-center justify-center"
            data-testid={`${testIdPrefix}-requester-avatar-container`}
          >
            <img
              src={requesterPhoto}
              alt={requesterName}
              className="w-full h-full object-cover"
              data-testid={`${testIdPrefix}-requester-avatar-img`}
            />
          </div>

          <div
            className="flex flex-col min-w-0 flex-1 justify-center gap-1"
            data-testid={`${testIdPrefix}-requester-details`}
          >
            <div
              className="flex items-center gap-1.5 flex-wrap min-w-0"
              data-testid={`${testIdPrefix}-requester-name-row`}
            >
              <InfoText
                text={requesterName}
                size="base"
                className="!font-bold !text-light-text-primary dark:!text-dark-text-primary truncate max-w-full"
                maxLength="FULL"
                data-testid={`${testIdPrefix}-requester-name`}
              />
              {requesterHandle && (
                <InfoText
                  text={`@${requesterHandle}`}
                  size="sm"
                  className="!text-light-text-primary dark:!text-dark-text-secondary font-normal"
                  maxLength="FULL"
                  data-testid={`${testIdPrefix}-requester-handle`}
                />
              )}
            </div>
            <InfoText
              text={formatCustomerType(requesterType, t) || '---'}
              size="xs"
              className="!text-text-secondary dark:!text-dark-text-secondary font-normal mt-0.5 truncate"
              maxLength="FULL"
              data-testid={`${testIdPrefix}-requester-role`}
            />
            <InfoText
              text={requesterEmail}
              size="xs"
              className="!text-light-text-secondary dark:!text-dark-text-secondary font-normal truncate"
              maxLength="FULL"
              data-testid={`${testIdPrefix}-requester-email`}
            />
          </div>
        </div>
      </div>

      {/* Request Applies To Card */}
      {showAppliesCard && (
        <div
          className="bg-soft-background dark:bg-dark-surface-secondary rounded-2xl p-4 sm:p-5 flex flex-col justify-between"
          data-testid={`${testIdPrefix}-applies-card`}
        >
          <div
            className="flex items-center gap-2 mb-3.5"
            data-testid={`${testIdPrefix}-applies-header`}
          >
            <ProfileTick
              size={20}
              variant="Bold"
              className="text-light-alert dark:text-dark-alert shrink-0"
              data-testid={`${testIdPrefix}-applies-icon`}
            />
            <TitleText
              text={t('support.requestAppliesTo')}
              size="base"
              className="!font-bold !text-light-text-primary dark:!text-dark-text-primary"
              data-testid={`${testIdPrefix}-applies-title`}
            />
          </div>

          <div
            className="flex items-center gap-3.5 min-w-0"
            data-testid={`${testIdPrefix}-applies-body`}
          >
            <div
              className="w-14 h-14 rounded-full overflow-hidden shrink-0 border border-gray-100 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 flex items-center justify-center"
              data-testid={`${testIdPrefix}-applies-avatar-container`}
            >
              <img
                src={sellerPhoto}
                alt={sellerName}
                className="w-full h-full object-cover"
                data-testid={`${testIdPrefix}-applies-avatar-img`}
              />
            </div>

            <div
              className="flex flex-col min-w-0 flex-1 justify-center gap-1"
              data-testid={`${testIdPrefix}-applies-details`}
            >
              <div
                className="flex items-center gap-1.5 flex-wrap min-w-0"
                data-testid={`${testIdPrefix}-applies-name-row`}
              >
                <InfoText
                  text={sellerName}
                  size="base"
                  className="!font-bold !text-light-text-primary dark:!text-dark-text-primary truncate max-w-full"
                  maxLength="FULL"
                  data-testid={`${testIdPrefix}-applies-name`}
                />
                {sellerHandle && (
                  <InfoText
                    text={`@${sellerHandle}`}
                    size="sm"
                    className="!text-light-text-primary dark:!text-dark-text-secondary font-normal"
                    maxLength="FULL"
                    data-testid={`${testIdPrefix}-applies-handle`}
                  />
                )}
              </div>
              <InfoText
                text={
                  sellerType
                    ? formatCustomerType(sellerType, t)
                    : t('support.sellerAccount')
                }
                size="xs"
                className="!text-text-secondary dark:!text-dark-text-secondary font-normal mt-0.5 truncate"
                maxLength="FULL"
                data-testid={`${testIdPrefix}-applies-role`}
              />
              <InfoText
                text={sellerEmail}
                size="xs"
                className="!text-light-text-secondary dark:!text-dark-text-secondary font-normal truncate"
                maxLength="FULL"
                data-testid={`${testIdPrefix}-applies-email`}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketUserCards;
